import React, { useState, useEffect } from 'react';
import { useStripe } from '../contexts/StripeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, getApiUrl } from '../config/api';

interface FileUploadProps {
  onSummaryGenerated: (summary: string, fullText: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  onFileSelected?: (file: File) => void;
}

export default function FileUpload({ onSummaryGenerated, setIsLoading, onFileSelected }: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const { credits, checkCredits } = useStripe();
  const { currentUser } = useAuth();

  useEffect(() => { checkCredits(); }, [checkCredits]);

  const acceptFile = (file: File) => {
    if (file.type === 'application/pdf') {
      setSelectedFile(file);
      onFileSelected?.(file);
      setError('');
    } else {
      setSelectedFile(null);
      setError('Only PDF files are supported.');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) acceptFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) acceptFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) { setError('Please select a file.'); return; }
    if (credits <= 0) { setError('You need credits to summarize a PDF.'); return; }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setIsLoading(true);
      setError('');
      const token = await currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(getApiUrl(API_ENDPOINTS.SUMMARIZE), {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to process file');
      }

      const data = await response.json();
      onSummaryGenerated(data.summary, data.full_text);
      checkCredits();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsLoading(false);
    }
  };

  const noCredits = credits <= 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Credits indicator */}
      <div className="flex items-center justify-between text-sm">
        <span className="theme-text-secondary">Available credits</span>
        <span className="font-semibold px-2 py-0.5 rounded-full text-xs"
          style={{
            background: noCredits ? 'rgba(239,68,68,0.1)' : 'var(--accent-light)',
            color: noCredits ? '#ef4444' : 'var(--accent-primary)',
          }}>
          {credits} credit{credits !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Drop zone */}
      <label
        htmlFor="pdf-upload"
        className={`upload-zone flex flex-col items-center justify-center w-full h-52 select-none ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-3 pointer-events-none">
          {selectedFile ? (
            <>
              <span className="text-4xl">📄</span>
              <p className="text-sm font-semibold theme-text-primary">{selectedFile.name}</p>
              <p className="text-xs theme-text-secondary">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · Click to change
              </p>
            </>
          ) : (
            <>
              <svg className="w-10 h-10" style={{ color: 'var(--accent-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm font-medium theme-text-primary">
                Drop your PDF here, or <span style={{ color: 'var(--accent-primary)' }}>browse</span>
              </p>
              <p className="text-xs theme-text-secondary">PDF files only · any size</p>
            </>
          )}
        </div>
        <input id="pdf-upload" type="file" accept=".pdf" className="hidden" onChange={handleChange} />
      </label>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1.5">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
          </svg>
          {error}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!selectedFile || noCredits}
        className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
        style={{
          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
          boxShadow: '0 4px 14px rgba(79,70,229,0.35)',
        }}
      >
        {noCredits ? 'Purchase credits to continue' : 'Upload & Summarize'}
      </button>

      {noCredits && (
        <p className="text-center text-xs theme-text-secondary">
          <a href="/pricing" style={{ color: 'var(--accent-primary)' }}>Buy credits</a> starting at $3
        </p>
      )}
    </form>
  );
}
